import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import api from 'api/client';
import { useAuth } from 'context/AuthContext';

const newUserState = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  role: 'user',
  is_active: true
};

const editUserState = {
  first_name: '',
  last_name: '',
  role: 'user',
  is_active: true
};

function extractErrorMessage(error, fallback) {
  const payload = error?.response?.data;
  if (!payload) return error?.message || fallback;
  if (typeof payload === 'string') return payload;
  if (payload.detail) return payload.detail;
  if (payload.message) return payload.message;
  const firstField = Object.keys(payload)[0];
  if (firstField) {
    const value = payload[firstField];
    if (Array.isArray(value)) return value[0];
    if (typeof value === 'string') return value;
  }
  return fallback;
}

const AdminUsersPage = () => {
  const { user: authUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(newUserState);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState(editUserState);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDialog, setBulkDialog] = useState({ open: false, action: null });
  const [bulkRunning, setBulkRunning] = useState(false);
  const [resetDialog, setResetDialog] = useState({ open: false, user: null });
  const [resetPassword, setResetPassword] = useState('');
  const [useAutoPassword, setUseAutoPassword] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchText.trim()), 250);
    return () => clearTimeout(timer);
  }, [searchText]);

  const showSnackbar = useCallback((severity, message) => {
    setSnackbar({ open: true, severity, message });
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (roleFilter !== 'all') params.set('role', roleFilter);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const { data } = await api.get(`/api/admin/users/${suffix}`);
      const list = Array.isArray(data) ? data : data?.results || [];
      setRows(list);
      setSelectedIds((prev) => prev.filter((id) => list.some((item) => item.id === id)));
    } catch (error) {
      showSnackbar('error', extractErrorMessage(error, 'Failed to load users.'));
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter, showSnackbar]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const stats = useMemo(() => {
    const total = rows.length;
    const admins = rows.filter((item) => item.role === 'admin').length;
    const active = rows.filter((item) => item.is_active).length;
    return {
      total,
      admins,
      active,
      inactive: total - active
    };
  }, [rows]);

  const allRowsSelected = rows.length > 0 && rows.every((row) => selectedIds.includes(row.id));
  const partiallySelected = selectedIds.length > 0 && !allRowsSelected;

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(rows.map((row) => row.id));
      return;
    }
    setSelectedIds([]);
  };

  const toggleSelectRow = (userId, checked) => {
    if (checked) {
      setSelectedIds((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
      return;
    }
    setSelectedIds((prev) => prev.filter((id) => id !== userId));
  };

  const handleCreate = async () => {
    if (!createForm.email.trim() || !createForm.password.trim()) {
      showSnackbar('error', 'Email and password are required.');
      return;
    }
    setCreating(true);
    try {
      await api.post('/api/admin/users/', createForm);
      showSnackbar('success', 'User created successfully.');
      setCreateOpen(false);
      setCreateForm(newUserState);
      await loadUsers();
    } catch (error) {
      showSnackbar('error', extractErrorMessage(error, 'Failed to create user.'));
    } finally {
      setCreating(false);
    }
  };

  const openEditDialog = (row) => {
    setEditingUser(row);
    setEditForm({
      first_name: row.first_name || '',
      last_name: row.last_name || '',
      role: row.role || 'user',
      is_active: Boolean(row.is_active)
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingUser) return;
    setEditing(true);
    try {
      const { data } = await api.patch(`/api/admin/users/${editingUser.id}/`, editForm);
      setRows((prev) => prev.map((row) => (row.id === editingUser.id ? data : row)));
      showSnackbar('success', 'User updated successfully.');
      setEditOpen(false);
      setEditingUser(null);
    } catch (error) {
      showSnackbar('error', extractErrorMessage(error, 'Failed to update user.'));
    } finally {
      setEditing(false);
    }
  };

  const handleToggleActive = async (row) => {
    try {
      const { data } = await api.patch(`/api/admin/users/${row.id}/`, {
        is_active: !row.is_active
      });
      setRows((prev) => prev.map((item) => (item.id === row.id ? data : item)));
    } catch (error) {
      showSnackbar('error', extractErrorMessage(error, 'Failed to update account status.'));
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    setDeleting(true);
    try {
      await api.delete(`/api/admin/users/${deleteUser.id}/`);
      setRows((prev) => prev.filter((row) => row.id !== deleteUser.id));
      setSelectedIds((prev) => prev.filter((id) => id !== deleteUser.id));
      showSnackbar('success', 'User deleted successfully.');
      setDeleteOpen(false);
      setDeleteUser(null);
    } catch (error) {
      showSnackbar('error', extractErrorMessage(error, 'Failed to delete user.'));
    } finally {
      setDeleting(false);
    }
  };

  const runBulkAction = async () => {
    if (!bulkDialog.action || selectedIds.length === 0) return;
    setBulkRunning(true);
    try {
      const { data } = await api.post('/api/admin/users/bulk_action/', {
        action: bulkDialog.action,
        user_ids: selectedIds
      });
      showSnackbar('success', data?.detail || 'Bulk action completed.');
      setBulkDialog({ open: false, action: null });
      setSelectedIds([]);
      await loadUsers();
    } catch (error) {
      showSnackbar('error', extractErrorMessage(error, 'Bulk action failed.'));
    } finally {
      setBulkRunning(false);
    }
  };

  const openResetDialog = (row) => {
    setResetDialog({ open: true, user: row });
    setUseAutoPassword(true);
    setResetPassword('');
    setGeneratedPassword('');
  };

  const copyGeneratedPassword = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword);
      showSnackbar('success', 'Temporary password copied.');
    } catch {
      showSnackbar('error', 'Could not copy password.');
    }
  };

  const handleResetPassword = async () => {
    if (!resetDialog.user) return;
    if (!useAutoPassword && !resetPassword.trim()) {
      showSnackbar('error', 'Enter a new password or use auto-generate.');
      return;
    }
    setResetting(true);
    try {
      const payload = useAutoPassword ? {} : { new_password: resetPassword };
      const { data } = await api.post(`/api/admin/users/${resetDialog.user.id}/reset_password/`, payload);
      if (data?.temporary_password) {
        setGeneratedPassword(data.temporary_password);
      } else {
        showSnackbar('success', data?.detail || 'Password reset successfully.');
        setResetDialog({ open: false, user: null });
        setResetPassword('');
      }
    } catch (error) {
      showSnackbar('error', extractErrorMessage(error, 'Failed to reset password.'));
    } finally {
      setResetting(false);
    }
  };

  const AMBER = '#f59e0b';
  const initial = (row) => (row.email || '?').charAt(0).toUpperCase();

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1.5}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Users
          </Typography>
          <Typography color="text.secondary">
            {loading && rows.length === 0
              ? 'Loading…'
              : `${stats.total} shown: ${stats.admins} admin, ${stats.active} active, ${stats.inactive} inactive`}
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => setCreateOpen(true)}
          sx={{ height: 44, fontWeight: 700, bgcolor: AMBER, color: '#1f1300', '&:hover': { bgcolor: '#d97706' } }}
        >
          Add user
        </Button>
      </Stack>

      <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          placeholder="Search by email, username or name"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          sx={{ minWidth: 280, flex: '1 1 280px', maxWidth: 440 }}
        />
        <ToggleButtonGroup size="small" exclusive value={roleFilter} onChange={(_, v) => v && setRoleFilter(v)}>
          <ToggleButton value="all">All roles</ToggleButton>
          <ToggleButton value="admin">Admin</ToggleButton>
          <ToggleButton value="user">User</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={statusFilter} onChange={(_, v) => v && setStatusFilter(v)}>
          <ToggleButton value="all">Any status</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
        </ToggleButtonGroup>
        <Button color="inherit" onClick={loadUsers} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {selectedIds.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
          sx={{ px: 2, py: 1.2, borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {selectedIds.length} selected
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button size="small" variant="outlined" color="inherit" onClick={() => setBulkDialog({ open: true, action: 'activate' })}>
              Activate
            </Button>
            <Button size="small" variant="outlined" color="warning" onClick={() => setBulkDialog({ open: true, action: 'deactivate' })}>
              Deactivate
            </Button>
            <Button size="small" variant="outlined" color="error" onClick={() => setBulkDialog({ open: true, action: 'delete' })}>
              Delete
            </Button>
            <Button size="small" color="inherit" onClick={() => setSelectedIds([])}>
              Clear
            </Button>
          </Stack>
        </Stack>
      )}

      <Box sx={{ borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper', overflow: 'hidden' }}>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="warning"
                    checked={allRowsSelected}
                    indeterminate={partiallySelected}
                    onChange={(event) => toggleSelectAll(event.target.checked)}
                    inputProps={{ 'aria-label': 'select all users' }}
                  />
                </TableCell>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Last sign-in</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No users match these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const fullName = [row.first_name, row.last_name].filter(Boolean).join(' ');
                  const isSelf = row.id === authUser?.id;
                  const admin = row.role === 'admin';
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="warning"
                          checked={selectedIds.includes(row.id)}
                          onChange={(event) => toggleSelectRow(row.id, event.target.checked)}
                          inputProps={{ 'aria-label': `select user ${row.email}` }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <Avatar sx={{ width: 30, height: 30, fontSize: 14, bgcolor: 'rgba(245, 158, 11, 0.16)', color: AMBER }}>
                            {initial(row)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.email}
                              {isSelf && (
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {' '}
                                  (you)
                                </Typography>
                              )}
                            </Typography>
                            {fullName && (
                              <Typography variant="caption" color="text.secondary">
                                {fullName}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 0.4,
                            px: 1.3,
                            py: 0.5,
                            borderRadius: 99,
                            bgcolor: admin ? 'rgba(245, 158, 11, 0.16)' : 'rgba(100, 116, 139, 0.16)',
                            color: admin ? AMBER : 'text.secondary'
                          }}
                        >
                          {admin ? 'ADMIN' : 'USER'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          color="warning"
                          checked={Boolean(row.is_active)}
                          disabled={isSelf}
                          onChange={() => handleToggleActive(row)}
                          inputProps={{ 'aria-label': `${row.is_active ? 'Deactivate' : 'Activate'} ${row.email}` }}
                        />
                      </TableCell>
                      <TableCell>{row.date_joined ? new Date(row.date_joined).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>{row.last_login ? new Date(row.last_login).toLocaleString() : 'Never'}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Button size="small" color="inherit" onClick={() => openEditDialog(row)}>
                          Edit
                        </Button>
                        <Button size="small" color="inherit" onClick={() => openResetDialog(row)}>
                          Reset password
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          disabled={isSelf}
                          title={isSelf ? 'You cannot delete your own account' : undefined}
                          onClick={() => {
                            setDeleteUser(row);
                            setDeleteOpen(true);
                          }}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={createOpen} onClose={() => (creating ? null : setCreateOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <TextField
              label="Email"
              type="email"
              value={createForm.email}
              onChange={(event) => setCreateForm((prev) => ({ ...prev, email: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Temporary Password"
              type="password"
              value={createForm.password}
              onChange={(event) => setCreateForm((prev) => ({ ...prev, password: event.target.value }))}
              fullWidth
              required
              helperText="Minimum 8 characters."
            />
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="First Name"
                  value={createForm.first_name}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, first_name: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Last Name"
                  value={createForm.last_name}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, last_name: event.target.value }))}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="create-role-label">Role</InputLabel>
                  <Select
                    labelId="create-role-label"
                    value={createForm.role}
                    label="Role"
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, role: event.target.value }))}
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={createForm.is_active}
                      onChange={(event) => setCreateForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                    />
                  }
                  label="Active account"
                  sx={{ mt: 1 }}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
            sx={{
              bgcolor: 'var(--fz-surface-1, #111827)',
              color: '#f59e0b',
              border: '1px solid #f59e0b',
              '&:hover': { bgcolor: '#0b1220', borderColor: '#f59e0b' }
            }}
          >
            {creating ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => (editing ? null : setEditOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="First Name"
                  value={editForm.first_name}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, first_name: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Last Name"
                  value={editForm.last_name}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, last_name: event.target.value }))}
                  fullWidth
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel id="edit-role-label">Role</InputLabel>
              <Select
                labelId="edit-role-label"
                value={editForm.role}
                label="Role"
                onChange={(event) => setEditForm((prev) => ({ ...prev, role: event.target.value }))}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={editForm.is_active}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                />
              }
              label="Active account"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={editing}
            sx={{
              bgcolor: 'var(--fz-surface-1, #111827)',
              color: '#f59e0b',
              border: '1px solid #f59e0b',
              '&:hover': { bgcolor: '#0b1220', borderColor: '#f59e0b' }
            }}
          >
            {editing ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => (deleting ? null : setDeleteOpen(false))} maxWidth="xs" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            You are deleting <strong>{deleteUser?.email || 'this user'}</strong>. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDialog.open}
        onClose={() => (bulkRunning ? null : setBulkDialog({ open: false, action: null }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Bulk Action</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Apply <strong>{bulkDialog.action || 'action'}</strong> to <strong>{selectedIds.length}</strong> selected user(s)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialog({ open: false, action: null })} disabled={bulkRunning}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={bulkDialog.action === 'delete' ? 'error' : 'inherit'}
            onClick={runBulkAction}
            disabled={bulkRunning}
            sx={
              bulkDialog.action === 'delete'
                ? undefined
                : {
                    bgcolor: 'var(--fz-surface-1, #111827)',
                    color: '#f59e0b',
                    border: '1px solid #f59e0b',
                    '&:hover': { bgcolor: '#0b1220', borderColor: '#f59e0b' }
                  }
            }
          >
            {bulkRunning ? 'Applying...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resetDialog.open}
        onClose={() => (resetting ? null : setResetDialog({ open: false, user: null }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              Account: <strong>{resetDialog.user?.email || '-'}</strong>
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={useAutoPassword}
                  onChange={(event) => {
                    setUseAutoPassword(event.target.checked);
                    setGeneratedPassword('');
                  }}
                />
              }
              label="Generate temporary password automatically"
            />

            {!useAutoPassword && (
              <TextField
                label="New password"
                type="password"
                value={resetPassword}
                onChange={(event) => setResetPassword(event.target.value)}
                helperText="Minimum 8 characters."
                fullWidth
              />
            )}

            {generatedPassword && (
              <Alert
                severity="success"
                action={
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    onClick={copyGeneratedPassword}
                  >
                    Copy
                  </Button>
                }
              >
                Temporary password: <strong>{generatedPassword}</strong>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setResetDialog({ open: false, user: null });
              setGeneratedPassword('');
              setResetPassword('');
            }}
            disabled={resetting}
          >
            {generatedPassword ? 'Close' : 'Cancel'}
          </Button>
          {!generatedPassword && (
            <Button
              variant="contained"
              onClick={handleResetPassword}
              disabled={resetting}
              sx={{
                bgcolor: 'var(--fz-surface-1, #111827)',
                color: '#f59e0b',
                border: '1px solid #f59e0b',
                '&:hover': { bgcolor: '#0b1220', borderColor: '#f59e0b' }
              }}
            >
              {resetting ? 'Resetting...' : 'Reset Password'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setSnackbar((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default AdminUsersPage;
